import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, FormControl, MenuItem, Select, Skeleton, Typography, Paper, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useSubmission } from 'services/Data/SubmissionServices';
import { useCreateSubmissionDetail, useUpdateSubmissionDetail } from 'services/Data/SubmissionDetailServices';
import { createSubmissionDetailPatchDocument } from 'services/Serializers/SubmissionDetailPatchDocument';
import { translateLabel } from 'services/Utilities/labelUtils';
import RenderItem from './RenderItem';
import { useVisibilityRefresh } from './VisibilityRefreshContext';

// Dropdown input with translated options and nested children
const SelectItem = ({ detail, allDetails = [], level = 0, isReadOnly = false }) => {
  const { submissionId } = useParams();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const visibilityRefresh = useVisibilityRefresh();
  const { data: submission, isLoading } = useSubmission(submissionId);
  const updateSubmissionDetailMutation = useUpdateSubmissionDetail(submissionId);
  const createSubmissionDetailMutation = useCreateSubmissionDetail(submissionId);

  const existingSubmissionDetail = submission?.submissionDetails?.find(sd => sd.criteriaTypeDetail_Id === detail.id);

  const sortedPossibleValues = useMemo(() => {
    const values = detail?.criteriaTypeDetailPossibleValues || detail?.CriteriaTypeDetailPossibleValues || [];
    return [...values].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [detail]);

  const getOptionRawValue = (option, fallbackIndex) => {
    if (option == null) return fallbackIndex ?? '';
    if (option.id !== undefined && option.id !== null) return option.id;
    if (option.value !== undefined && option.value !== null) return option.value;
    if (option.code !== undefined && option.code !== null) return option.code;
    if (option.key !== undefined && option.key !== null) return option.key;
    if (option.order !== undefined && option.order !== null) return option.order;
    return fallbackIndex ?? '';
  };

  const possibleValueOptions = useMemo(() => {
    return sortedPossibleValues.map((option, index) => {
      const rawValue = getOptionRawValue(option, index);
      const value = rawValue === undefined || rawValue === null ? '' : String(rawValue);
      return { option, rawValue, value, index };
    });
  }, [sortedPossibleValues]);

  const resolveInitialValue = useCallback(() => {
    const rawValue = existingSubmissionDetail?.value ?? existingSubmissionDetail?.value;
    if (rawValue === null || rawValue === undefined) return '';
    return String(rawValue);
  }, [existingSubmissionDetail?.value, existingSubmissionDetail?.value]);

  const [selectedValue, setSelectedValue] = useState(resolveInitialValue);

  useEffect(() => {
    setSelectedValue(resolveInitialValue());
  }, [resolveInitialValue]);

  const handleChange = async event => {
    if (isReadOnly) return;
    const newValue = event.target.value;

    const hasVisibilityRulesAttached = (allDetails || []).some(otherDetail => {
      const rules = otherDetail?.criteriaTypeDetailVisibilityRules || [];
      return rules.some(rule => rule?.controllingCriteriaTypeDetail_Id === detail.id);
    });

    setSelectedValue(newValue);

    if (hasVisibilityRulesAttached && visibilityRefresh?.setIsVisibilityRefreshInProgress) {
      visibilityRefresh.setIsVisibilityRefreshInProgress(true);
    }

    try {
      if (existingSubmissionDetail) {
        const patchDoc = createSubmissionDetailPatchDocument({ value: newValue });
        await updateSubmissionDetailMutation.mutateAsync({
          submissionDetailId: existingSubmissionDetail.id,
          patchDocument: patchDoc,
        });
      } else if (newValue !== '') {
        const newSubmissionDetail = {
          id: crypto.randomUUID(),
          submission_Id: submissionId,
          criteriaTypeDetail_Id: detail.id,
          description: '',
          value: newValue,
        };
        await createSubmissionDetailMutation.mutateAsync(newSubmissionDetail);
      }

      if (hasVisibilityRulesAttached) {
        await queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
      }
    } catch (error) {
      console.error('Failed to update select value:', error);
      setSelectedValue(resolveInitialValue());
    } finally {
      if (hasVisibilityRulesAttached && visibilityRefresh?.setIsVisibilityRefreshInProgress) {
        visibilityRefresh.setIsVisibilityRefreshInProgress(false);
      }
    }
  };

  const indentAmount = (level - 1) * 4;

  const childDetails = useMemo(() => {
    return allDetails
      .filter(childDetail => childDetail.parentCriteriaTypeDetail_Id === detail.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [allDetails, detail.id]);

  if (isLoading) {
    return (
      <Box sx={{ pl: indentAmount, py: 1 }} data-tour="submissionRequiredDropdown">
        <Skeleton variant="rounded" height={56} animation="wave" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ pl: indentAmount, py: 1 }} data-tour="submissionRequiredDropdown">
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.background.paper),
            border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : theme.palette.divider}`,
          }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: level === 0 ? 700 : 600,
              mb: 1,
              color: theme => theme.palette.text.primary,
            }}>
            {translateLabel(detail.name, i18n.language)}
          </Typography>
          {detail.description && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1.5,
                color: theme => theme.palette.text.secondary,
              }}>
              {translateLabel(detail.description, i18n.language)}
            </Typography>
          )}
          <FormControl
            fullWidth
            disabled={isReadOnly || updateSubmissionDetailMutation.isPending}
            required={detail?.isRequired !== false}>
            <Select
              value={selectedValue}
              onChange={handleChange}
              displayEmpty
              required={detail?.isRequired !== false}
              inputProps={{ readOnly: isReadOnly }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: '70vh',
                    maxWidth: 'min(92vw, 520px)',
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      alignItems: 'flex-start',
                      lineHeight: 1.35,
                      py: 1.25,
                    },
                  },
                },
              }}
              sx={{
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'),
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.45)' : theme.palette.divider),
                  borderWidth: 2,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme => theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme => theme.palette.primary.main,
                  boxShadow: theme => `0 0 0 2px ${theme.palette.primary.main}33`,
                },
              }}>
              <MenuItem value="">
                <em>{translateLabel(detail.placeholder, i18n.language) || t('common.selectOption')}</em>
              </MenuItem>
              {possibleValueOptions.map(({ option, value: optionValue, rawValue, index }) => {
                const optionLabel = translateLabel(option.name, i18n.language) || optionValue;
                return (
                  <MenuItem
                    key={option.id ?? rawValue ?? index}
                    value={optionValue}
                    sx={{ whiteSpace: 'normal', alignItems: 'flex-start', lineHeight: 1.35 }}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Paper>
      </Box>

      {childDetails.length > 0 && (
        <Box>
          {childDetails.map(childDetail => (
            <RenderItem key={childDetail.id} detail={childDetail} allDetails={allDetails} isReadOnly={isReadOnly} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SelectItem;
